import { absoluteUrl, SITE_URL } from '../Seo/Seo';
import posts from './BlogPostsContent';

export const POSTS_PER_PAGE = 9;

export const blogPosts = posts
  .filter((post) => post && post.id && post.date)
  .sort((a, b) => new Date(b.date) - new Date(a.date));

export const getBlogPagePath = (page) => (page === 1 ? '/blog' : `/blog/page/${page}`);

export const getPostPath = (post) => `/blogs/${post.id}`;

export const stripHtml = (value = '') =>
  value.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

export const decodeHtmlEntities = (value = '') =>
  value
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&#(\d+);/g, (match, code) => String.fromCharCode(Number(code)))
    .replace(/&#x([0-9a-f]+);/gi, (match, code) => String.fromCharCode(parseInt(code, 16)));

export const getReadTime = (post) => {
  const text = stripHtml(post.content || post.summary);
  const cjkCharacters = (text.match(/[\u3400-\u9fff]/g) || []).length;
  const latinWords = text
    .replace(/[\u3400-\u9fff]/g, ' ')
    .split(/\s+/)
    .filter(Boolean).length;
  const readingUnits = latinWords + Math.ceil(cjkCharacters / 2);
  return `${Math.max(2, Math.ceil(readingUnits / 220))} min read`;
};

export const getIsoDate = (value) => {
  const parsedDate = new Date(value);
  return Number.isNaN(parsedDate.getTime())
    ? new Date().toISOString().split('T')[0]
    : parsedDate.toISOString().split('T')[0];
};

export const getArticleSection = (post) => {
  if (post.category) return post.category;

  const text = `${post.title} ${post.summary} ${post.metaTags?.keywords || ''}`.toLowerCase();

  if (text.includes('pva')) return 'Formula Innovation';
  if (text.includes('packaging') || text.includes('artwork') || text.includes('dieline')) return 'Packaging';
  if (text.includes('refill') || text.includes('bulk')) return 'Refill and Bulk Supply';
  if (text.includes('expo') || text.includes('show') || text.includes('issa')) return 'Industry Events';
  if (text.includes('made') || text.includes('origin') || text.includes('factory') || text.includes('production')) return 'Manufacturing';
  if (text.includes('private label') || text.includes('brand') || text.includes('launch')) return 'Private Label Launch';
  if (text.includes('plastic') || text.includes('sustain') || text.includes('eco')) return 'Sustainability';

  return 'Laundry Detergent Sheets';
};

export const getExcerpt = (post) => {
  const text = stripHtml(post.summary || post.metaTags?.description || post.content);
  return text.length > 155 ? `${text.slice(0, 152).trim()}...` : text;
};

const textForPost = (post) =>
  `${post.title} ${post.summary} ${post.content || ''} ${post.metaTags?.keywords || ''}`.toLowerCase();

export const getPostFaqs = (post) => {
  if (post.id === 'custom-fragrance-laundry-detergent-sheets') {
    return [
      {
        question: 'What is the MOQ for custom-fragrance laundry detergent sheets?',
        answer: 'The minimum order is 150,000 sheets per custom fragrance. Final packaging and SKU allocations should be confirmed in the production quotation.',
      },
      {
        question: 'How many retail units does 150,000 sheets produce?',
        answer: 'At 50 sheets per box or pouch, 150,000 sheets produce 3,000 units. At 30 sheets per unit, they produce 5,000 units.',
      },
      {
        question: 'Can I provide my own proprietary fragrance?',
        answer: 'Yes. A customer-owned fragrance can be shipped to the manufacturing site, and Klean Leaf can help coordinate the logistics. Carrier acceptance, documentation, and import requirements are reviewed for each shipment.',
      },
      {
        question: 'Can Klean Leaf purchase a fragrance using its supplier code?',
        answer: 'Klean Leaf can attempt to source an identified fragrance from the relevant fragrance house when the buyer provides the complete code and supplier information. Availability, commercial terms, and any authorization requirements still apply.',
      },
      {
        question: 'Can Klean Leaf match an existing scent?',
        answer: 'Klean Leaf\'s three-person R&D sensory team can evaluate a reference and develop a close sensory direction. This is not a claim that the resulting fragrance is chemically identical to another company\'s protected formula.',
      },
      {
        question: 'How much fragrance is needed for sampling?',
        answer: 'A typical custom-fragrance sample project uses approximately 100 g of liquid fragrance concentrate.',
      },
      {
        question: 'How much does custom-fragrance sampling cost?',
        answer: 'Klean Leaf\'s current sampling fee is $399, and the customer typically receives approximately 50–100 finished sheets. The quotation should confirm freight, revisions, and any special sourcing charges.',
      },
      {
        question: 'Are inventory fragrances available at a lower MOQ?',
        answer: 'Yes. Existing Klean Leaf inventory fragrance programs generally have lower minimums than a fully customized fragrance. Confirm the applicable MOQ for the selected scent and package configuration.',
      },
    ];
  }

  if (post.id === 'best-laundry-detergent-sheet-manufacturers-2026') {
    return [
      {
        question: 'Who is the best laundry detergent sheets manufacturer for private label in 2026?',
        answer: 'There is no universal best manufacturer. Klean Leaf is a strong fit for North American buyers seeking a 1,000-retail-package starting MOQ and coordinated formula, packaging, sample, production, and logistics support. Buyers prioritizing Canadian, EU, Indian, or Chinese production should compare the location-specific candidates in this guide.',
      },
      {
        question: 'What is a typical MOQ for private label laundry detergent sheets?',
        answer: 'MOQ varies by supplier and must be defined carefully. Ask separately for the formula batch, sheet-converting run, printed packaging, and finished quantity for each SKU, then convert every quote into sheets, retail packages, and cases. Klean Leaf starts at 1,000 finished retail package units per SKU.',
      },
      {
        question: 'What documents should a laundry sheet manufacturer provide?',
        answer: 'Request formula-specific SDS and ingredient information, product specifications, batch-release or COA criteria, stability and packaging-compatibility evidence, applicable test reports, and current facility certificates. Verify the legal entity, address, product scope, formula code, issuing body, and validity dates.',
      },
      {
        question: 'How should I test private label laundry detergent sheet samples?',
        answer: 'Test the proposed dose across relevant water temperatures, water hardness, load sizes, cycles, machines, fabrics, and soils. Record dissolution, residue, stain and odor removal, fragrance after drying, sheet strength, perforation, and stability in the final package.',
      },
      {
        question: 'Are Klean Leaf laundry detergent sheets made in the USA?',
        answer: 'No. Klean Leaf client support and sample coordination are based in Columbus, Ohio, while current laundry detergent sheet production is handled in China. Klean Leaf states this distinction clearly so buyers can plan labeling, import, and country-of-origin requirements accurately.',
      },
    ];
  }

  if (post.id === 'are-aluminum-bottles-more-eco-friendly-than-plastic') {
    return [
      {
        question: 'Are aluminum bottles always more eco-friendly than plastic bottles?',
        answer: 'Not always. Aluminum can reduce plastic use, but primary aluminum production is highly energy-intensive. The result depends on recycled content, bottle weight, refill cycles, transportation, and recycling.',
      },
      {
        question: 'Are glass bottles automatically more sustainable than aluminum or plastic?',
        answer: 'Not automatically. Glass can be recycled or reused, but it is usually heavier and more fragile. It may perform better when it is genuinely reused many times and supported by an efficient refill and recovery system.',
      },
      {
        question: 'Why can laundry sheets have a lower packaging and shipping burden?',
        answer: 'Laundry sheets remove most of the water found in liquid detergent. They are lighter and thinner, and they can use paper boxes or paper bags, so packaging and transportation can require fewer resources for the same number of washes.',
      },
      {
        question: 'Is PVA a microplastic?',
        answer: 'There is currently no professional evidence proving that detergent-grade PVA automatically becomes microplastics. PVA is a water-soluble synthetic polymer, but its grade, formula, dissolving behavior, and wastewater conditions still deserve clear evidence.',
      },
      {
        question: 'How should brands compare laundry packaging options?',
        answer: 'Compare packaging per wash rather than by material name alone. Review recycled content, package weight, transportation, refill use, local recovery, product concentration, and the amount consumers actually use.',
      },
    ];
  }

  if (post.id === 'rise-of-detergent-sheets') {
    return [
      {
        question: 'Why are brands interested in laundry detergent sheets?',
        answer: 'Brands are interested in the format because it is compact, lightweight, simple to portion, and compatible with smaller paper-packaging formats. The product may also be easier to store and ship than bottled liquid detergent.',
      },
      {
        question: 'Do laundry detergent sheets work for every type of laundry?',
        answer: 'A well-developed sheet can work well for normal household laundry. Very large or heavily soiled loads may require a higher dosage or pre-treatment, depending on the formula and washing conditions.',
      },
      {
        question: 'Can laundry sheets be packed in paper packaging?',
        answer: 'Yes. Common options include paper envelopes, folding cartons, drawer boxes, sample packs, and bulk refill cartons. The appropriate package depends on the sheet count, retail channel, and required product information.',
      },
      {
        question: 'Should brands test samples before ordering?',
        answer: 'Yes. Buyers should test cleaning performance, dissolving behavior, sheet strength, fragrance, perforation, and product condition before confirming production.',
      },
      {
        question: 'Can Klean Leaf support private-label packaging?',
        answer: 'Yes. Klean Leaf can help review package options, confirm dimensions, provide dielines, review artwork, produce physical packaging samples, and coordinate final production packing.',
      },
    ];
  }

  if (post.id === 'year-of-innovation-in-laundry-care') {
    return [
      {
        question: 'What changed in laundry care in 2024?',
        answer: 'Klean Leaf saw buyers ask more practical questions about formula performance, sheet count, packaging structure, fragrance, sample testing, and how products would fit specific sales channels.',
      },
      {
        question: 'Why did packaging become more important earlier in the project?',
        answer: 'Package format affects sheet count, dielines, labeling space, carton planning, and sample approval, so discussing packaging early helps avoid artwork and production delays.',
      },
      {
        question: 'Why is sample testing important for laundry detergent sheets?',
        answer: 'Samples help buyers review sheet feel, dissolving behavior, fragrance, flexibility, and cleaning performance before choosing a formula for production.',
      },
      {
        question: 'What should private-label buyers clarify before production?',
        answer: 'Buyers should clarify the target customer, sales channel, formula direction, sheet count, package type, label information, barcode needs, carton plan, and delivery requirements.',
      },
    ];
  }

  if (post.id === 'long-lasting-fragrance-laundry-detergent-sheets') {
    return [
      {
        question: 'Can laundry detergent sheets hold scent as long as fabric softener?',
        answer: 'Not in exactly the same way. Fabric softener is usually dosed in a larger amount and often enters the rinse cycle, while a detergent sheet is smaller and works in the wash. A well-designed hybrid fragrance system can still create meaningful dry-fabric scent.',
      },
      {
        question: 'Do encapsulated fragrances work in cold water?',
        answer: 'They can. Many fabric-care capsule systems are designed around fabric deposition and later friction, not simply heat. Cold-water performance still depends on the detergent formula, capsule type, fabric, wash conditions, and dosage.',
      },
      {
        question: 'Can Klean Leaf support fragrance-free and scented sheets in the same program?',
        answer: 'Yes. Many private-label programs include both fragrance-free and scented versions. They should be treated as separate SKUs with separate artwork, production planning, and sample approval.',
      },
      {
        question: 'How many sampling rounds should a custom scent require?',
        answer: 'Plan for more than one. The first round usually establishes direction. Later rounds may adjust intensity, package scent, dry-fabric performance, free-fragrance balance, encapsulated fragrance use, or stability after storage.',
      },
      {
        question: 'Is encapsulated fragrance compatible with PVA-free sheets?',
        answer: 'It may be possible, but it should be reviewed as a formulation project. Film chemistry, capsule chemistry, processing conditions, fragrance load, dissolving behavior, and finished-sheet stability all need to work together.',
      },
    ];
  }

  const text = textForPost(post);

  if (text.includes('country of origin') || text.includes('made in china') || text.includes('factory')) {
    return [
      {
        question: 'Where are Klean Leaf laundry detergent sheets manufactured?',
        answer: 'Klean Leaf client support is based in Columbus, Ohio, and current laundry detergent sheet manufacturing is handled in China by an experienced factory team.',
      },
      {
        question: 'Why is country-of-origin transparency important for laundry sheets?',
        answer: 'Private label buyers need accurate origin information for packaging, retail review, customs, and customer trust before launching a detergent sheet product.',
      },
      {
        question: 'What parts of laundry sheet production require quality control?',
        answer: 'Formula mixing, drying, cutting, perforation, scent separation, package appearance, carton labels, and final packing all need quality review.',
      },
      {
        question: 'Can Klean Leaf support custom private label packaging?',
        answer: 'Yes. Klean Leaf can support package style selection, dielines, artwork review, package samples, production packing, and carton planning.',
      },
    ];
  }

  if (text.includes('clean') || text.includes('enzyme') || text.includes('formula')) {
    return [
      {
        question: 'Do laundry detergent sheets clean as well as liquid detergent?',
        answer: 'A well-developed sheet can clean everyday laundry effectively, but performance depends on the formula, active ingredients, load size, water conditions, and dosage instructions.',
      },
      {
        question: 'Why do enzymes matter in laundry detergent sheets?',
        answer: 'Enzymes can help target common soils such as body oil, food residue, sweat, and protein-based stains, which can improve real-world cleaning performance.',
      },
      {
        question: 'Can brands choose different laundry sheet formulas?',
        answer: 'Yes. Klean Leaf can review fragrance-free, scented, enzyme-supported, low-PVA, PVA-free, and other formula directions depending on the project.',
      },
      {
        question: 'Should brands test laundry sheet samples before ordering?',
        answer: 'Yes. Sample review helps buyers compare sheet feel, dissolving behavior, fragrance, cleaning expectations, and packaging direction before production.',
      },
    ];
  }

  if (text.includes('pva')) {
    return [
      {
        question: 'What is PVA in laundry detergent sheets?',
        answer: 'PVA, or polyvinyl alcohol, is commonly used to help form detergent sheets and support sheet strength, flexibility, and dissolving behavior.',
      },
      {
        question: 'Does Klean Leaf offer PVA-free laundry sheets?',
        answer: 'Klean Leaf has developed PVA-free laundry sheet options. MOQ starts at 1,000 retail package units per SKU, while formula direction, availability, and lead time should be reviewed with the team for each project.',
      },
      {
        question: 'Why are PVA-free sheets harder to manufacture?',
        answer: 'Removing PVA changes the sheet structure, so the formula still needs to stay stable, dissolve properly, clean well, and survive packing and shipping.',
      },
      {
        question: 'Can private label brands request low-PVA or PVA-free options?',
        answer: 'Yes. Klean Leaf can discuss low-PVA and PVA-free directions during formula and sample review.',
      },
    ];
  }

  if (text.includes('refill') || text.includes('bulk')) {
    return [
      {
        question: 'Why do refill shops use bulk laundry detergent sheets?',
        answer: 'Bulk laundry sheets are lightweight, compact, easy to portion, and easier to store than liquid detergent, making them practical for refill and zero-waste stores.',
      },
      {
        question: 'Can Klean Leaf supply unpacked or bulk laundry sheets?',
        answer: 'Yes. Klean Leaf can discuss bulk refill cartons along with retail-ready private label packaging depending on the buyer program.',
      },
      {
        question: 'Are bulk laundry sheets customizable?',
        answer: 'Formula, fragrance, sheet size, carton planning, and private label requirements can be reviewed during sampling and order planning.',
      },
      {
        question: 'What should refill shops confirm before ordering?',
        answer: 'Shops should review sheet count, fragrance, storage setup, carton quantity, customer instructions, and sample approval before production.',
      },
    ];
  }

  if (text.includes('expo') || text.includes('show') || text.includes('issa')) {
    return [
      {
        question: 'Why does Klean Leaf attend industry trade shows?',
        answer: 'Trade shows help the team meet buyers, understand category trends, and discuss private label laundry sheet needs with retailers, distributors, and refill shops.',
      },
      {
        question: 'Can buyers request samples after meeting Klean Leaf at a show?',
        answer: 'Yes. Buyers can contact the Columbus, Ohio support team to discuss samples, packaging direction, MOQ, and project timing.',
      },
      {
        question: 'What should buyers prepare before a trade show follow-up?',
        answer: 'It helps to prepare target sheet count, fragrance preference, packaging style, order quantity, sales channel, and launch timeline.',
      },
      {
        question: 'Does Klean Leaf support private label programs discussed at shows?',
        answer: 'Yes. Klean Leaf can support formula review, fragrance options, packaging, production, quality control, and delivery planning.',
      },
    ];
  }

  if (text.includes('launch') || text.includes('brand') || text.includes('private label') || text.includes('moq')) {
    return [
      {
        question: 'How do I start a private label laundry detergent sheets project?',
        answer: 'Start by sharing your product goals, sales channel, fragrance direction, packaging preference, target quantity, and timeline with the Klean Leaf team.',
      },
      {
        question: 'What is the MOQ for private label laundry sheets?',
        answer: 'MOQ starts at 1,000 retail package units per SKU.',
      },
      {
        question: 'Can Klean Leaf help with laundry sheets packaging options?',
        answer: 'Yes. Klean Leaf can discuss paper boxes, paper envelopes, retail packaging, bulk refill cartons, dielines, artwork review, and package samples.',
      },
      {
        question: 'How long does a first private label order take?',
        answer: 'Timing depends on formula, package approval, production scheduling, quality checks, and delivery planning. First orders generally need more time than repeat orders.',
      },
      {
        question: 'Should I approve samples before production?',
        answer: 'Yes. Product, fragrance, and package sample approval helps reduce launch risk before the first production order begins.',
      },
    ];
  }

  return [
    {
      question: 'Why are laundry detergent sheets popular with brands?',
      answer: 'Laundry sheets are compact, lightweight, easy to ship, and practical for private label, refill, retail, DTC, and wholesale programs.',
    },
    {
      question: 'Can laundry sheets use paper-based packaging?',
      answer: 'Yes. Many private label programs use paper boxes, paper envelopes, or other retail-ready packaging directions depending on the product plan.',
    },
    {
      question: 'Can brands request fragrance-free laundry sheets?',
      answer: 'Yes. Klean Leaf can discuss scented and fragrance-free options during product development and sample review.',
    },
    {
      question: 'How can buyers request laundry sheet samples?',
      answer: 'Buyers can contact Klean Leaf to review available samples, formula direction, fragrance options, packaging, MOQ, and project timing.',
    },
  ];
};

export const getRelatedPosts = (post, limit = 3) => {
  const section = getArticleSection(post);
  const sourceWords = new Set(
    textForPost(post)
      .split(/[^a-z0-9]+/)
      .filter((word) => word.length > 4)
  );

  return blogPosts
    .filter((candidate) => candidate.id !== post.id)
    .map((candidate) => {
      const candidateWords = textForPost(candidate).split(/[^a-z0-9]+/);
      const sharedWords = candidateWords.filter((word) => sourceWords.has(word)).length;
      const sectionScore = getArticleSection(candidate) === section ? 10 : 0;
      return { candidate, score: sectionScore + sharedWords };
    })
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map(({ candidate }) => candidate);
};

const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const internalLinkRules = [
  { phrase: 'custom packaging', href: '/production' },
  { phrase: 'packaging options', href: '/production' },
  { phrase: 'package artwork', href: '/production' },
  { phrase: 'dieline', href: '/production' },
  { phrase: 'formula development', href: '/production' },
  { phrase: 'quality control', href: '/production' },
  { phrase: 'production support', href: '/production' },
  { phrase: 'bulk refill cartons', href: '/solution' },
  { phrase: 'bulk laundry sheets', href: '/solution' },
  { phrase: 'refill shops', href: '/solution' },
  { phrase: 'MOQ', href: '/solution' },
  { phrase: 'sample approval', href: '/solution' },
  { phrase: 'request samples', href: '/contact' },
  { phrase: 'samples', href: '/contact' },
  { phrase: 'Columbus, Ohio', href: '/about' },
];

// Product links are curated in each article. Broad keyword insertion can link
// competitors' products or send a specific formula mention to the full catalog.

const addInternalLinks = (html) => {
  const linkedHrefs = new Set();
  let totalLinks = 0;
  let inAnchor = false;
  let inHeading = false;

  return html
    .split(/(<[^>]+>)/g)
    .map((segment) => {
      if (!segment) return segment;

      if (segment.startsWith('<')) {
        if (/^<a\b/i.test(segment)) inAnchor = true;
        if (/^<\/a>/i.test(segment)) inAnchor = false;
        if (/^<h[1-6]\b/i.test(segment)) inHeading = true;
        if (/^<\/h[1-6]>/i.test(segment)) inHeading = false;
        return segment;
      }

      if (inAnchor || inHeading || totalLinks >= 5) return segment;

      let text = segment;

      internalLinkRules.forEach(({ phrase, href }) => {
        if (totalLinks >= 5 || linkedHrefs.has(href) || !text.trim()) return;

        const pattern = new RegExp(`(^|[^\\w/])(${escapeRegExp(phrase)})(?![^<]*>|[^<>]*</a>)(?![\\w-])`, 'i');
        if (!pattern.test(text)) return;

        text = text.replace(pattern, (match, prefix, label) => {
          totalLinks += 1;
          linkedHrefs.add(href);
          return `${prefix}<a href="${href}">${label}</a>`;
        });
      });

      return text;
    })
    .join('');
};

export const addSafeBlankTargetRel = (html = '') =>
  html.replace(/<a(?![^>]*\brel=)([^>]*\btarget=["']_blank["'][^>]*)>/gi, '<a$1 rel="noopener noreferrer">');

export const slugifyHeading = (value) =>
  decodeHtmlEntities(stripHtml(value))
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');

const getAttributeValue = (attrs = '', name) => {
  const pattern = new RegExp(`\\s${name}=(["'])(.*?)\\1`, 'i');
  const match = attrs.match(pattern);
  return match ? match[2] : '';
};

export const getTableOfContents = (html) => {
  const items = [];
  const seen = new Map();
  const headingPattern = /<h([23])([^>]*)>([\s\S]*?)<\/h[23]>/gi;
  let match;

  while ((match = headingPattern.exec(html)) !== null) {
    const label = decodeHtmlEntities(stripHtml(match[3]));
    if (!label) continue;

    const existingId = getAttributeValue(match[2], 'id');
    let id = existingId;

    if (!id) {
      const baseId = slugifyHeading(label);
      const count = seen.get(baseId) || 0;
      seen.set(baseId, count + 1);
      id = count ? `${baseId}-${count + 1}` : baseId;
    }

    items.push({
      id,
      label,
      level: Number(match[1]),
    });
  }

  return items;
};

export const injectHeadingIds = (html) => {
  const seen = new Map();

  return html.replace(/<h([23])([^>]*)>([\s\S]*?)<\/h[23]>/gi, (match, level, attrs, labelHtml) => {
    if (/\sid=/i.test(attrs)) return match;

    const baseId = slugifyHeading(labelHtml);
    const count = seen.get(baseId) || 0;
    seen.set(baseId, count + 1);
    const id = count ? `${baseId}-${count + 1}` : baseId;

    return `<h${level}${attrs} id="${id}">${labelHtml}</h${level}>`;
  });
};

export const enhancePostContent = (post) =>
  injectHeadingIds(addInternalLinks(addSafeBlankTargetRel(post.content || '')));

export const blogListSchema = (pagePosts, currentPage) => ({
  '@context': 'https://schema.org',
  '@type': 'Blog',
  name: 'Klean Leaf Blog',
  description:
    'Insights on laundry detergent sheets, private label manufacturing, formula development, packaging options, samples, MOQ, and product launch support.',
  url: `${SITE_URL}${getBlogPagePath(currentPage)}`,
  blogPost: pagePosts.map((post) => ({
    '@type': 'BlogPosting',
    headline: post.title,
    description: post.metaTags?.description || post.summary,
    url: `${SITE_URL}${getPostPath(post)}`,
    image: absoluteUrl(post.imageUrl1),
    datePublished: getIsoDate(post.date),
    dateModified: getIsoDate(post.updatedDate || post.date),
    author: {
      '@type': 'Organization',
      name: 'Klean Leaf Team',
    },
  })),
});

export const articleSchema = (post) => ({
  '@context': 'https://schema.org',
  '@type': 'BlogPosting',
  headline: post.title,
  description: post.metaTags?.description || post.summary,
  image: absoluteUrl(post.imageUrl1),
  author: {
    '@type': 'Organization',
    name: 'Klean Leaf Team',
  },
  publisher: {
    '@type': 'Organization',
    name: 'Klean Leaf',
    logo: {
      '@type': 'ImageObject',
      url: `${SITE_URL}/logo.webp`,
    },
  },
  datePublished: getIsoDate(post.date),
  dateModified: getIsoDate(post.updatedDate || post.date),
  mainEntityOfPage: {
    '@type': 'WebPage',
    '@id': `${SITE_URL}${getPostPath(post)}`,
  },
  articleSection: getArticleSection(post),
  keywords: post.metaTags?.keywords,
});
